import { type ComponentOverrides } from "theme/theme.types";

import {
  CHECKBOX_BLANK_BOX,
  CHECKBOX_BOX_CLASS,
  CHECKBOX_CHECK_BOX,
  CHECKBOX_GLYPH_CLASS,
  CHECKBOX_MINUS_BOX,
} from "theme/components/checkbox-glyphs";
import { sizeVariants } from "theme/theme.helpers";

/**
 * Keyed on MUI's size names, not Apollo's, so `ui/select`'s raw `MuiCheckbox size="small"` rows get
 * the redesign for free. Figma's tokens sit one name up: its `checkbox/large|medium|small/*` are
 * `medium|small|xsmall` here. `glyph` is not derivable from `box` — 4px inside at medium, 3px at small,
 * 2px at xsmall.
 *
 * `affordance` is Figma's `container/p`, the ring the halo is painted in. MUI's input fills the root
 * it pads, so it doubles as the pointer target: 24px even around the 16px box.
 */
const CHECKBOX_SIZES = {
  medium: { box: 24, glyph: 16, affordance: 8 },
  small: { box: 20, glyph: 14, affordance: 6 },
  xsmall: { box: 16, glyph: 12, affordance: 4 },
} as const;

/** Only what changes with size, so MUI's native `large` still gets a painted box. */
const checkboxSizeVariants = sizeVariants(CHECKBOX_SIZES, (size) => {
  const { box, glyph, affordance } = CHECKBOX_SIZES[size];

  return {
    padding: affordance,
    // Negated, so the halo paints outside the box and costs nothing in layout.
    margin: -affordance,

    [`& .${CHECKBOX_BOX_CLASS}`]: {
      width: box,
      height: box,
    },
    [`& .${CHECKBOX_GLYPH_CLASS}`]: {
      width: glyph,
      height: glyph,
    },
  };
});

/**
 * Figma `Checkbox/Form control label` (node `14798:14313`, page `23754:13369`).
 *
 * **The division of labour is the whole design of this entry.** The halo is the root's
 * `background-color`, a circle through MUI's own `border-radius: 50%`, so the square lives on a
 * *child*, `.ApolloCheckbox-box`. Painting the box on the root is why the first redesign shipped with
 * no hover — the fill *occupies* the property the halo needs. `::before` would free it, but jsdom
 * cannot read pseudo-element styles and the state matrix would lose its measurements.
 *
 * Figma draws hover, focus and press as one halo. The ripple stays MUI's and fills the same circle over
 * it; MUI's own `alpha(…, hoverOpacity)` hover tint comes with the ripple, and the halo out-paints it on
 * source order, because theme overrides serialize after the component's variants. Through the negative
 * margin both bleed `affordance` past the box, into the label gap, leaving Figma's 4px between halo and text.
 * `&.Mui-disabled` comes *after* the selected blocks on purpose — equal specificity, later rule
 * paints. Note `indeterminate` is **not** a global state slot: `Mui-indeterminate` compiles, emits
 * valid CSS, and matches nothing.
 */
export const MuiCheckbox: ComponentOverrides["MuiCheckbox"] = {
  defaultProps: {
    // `icon` has to be a clonable element, so the blank entry still renders the box. MUI puts the
    // indeterminate icon in *both* slots, which is why the bar wins over the tick.
    icon: CHECKBOX_BLANK_BOX,
    checkedIcon: CHECKBOX_CHECK_BOX,
    indeterminateIcon: CHECKBOX_MINUS_BOX,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      variants: checkboxSizeVariants,

      // Before `:active` and the focus block: on equal specificity they must out-paint the touch reset.
      "&:hover": {
        backgroundColor: theme.palette.primary.lighter,
        // A tap leaves `:hover` stuck on touch screens; `:active` still answers the press.
        "@media (hover: none)": {
          backgroundColor: "transparent",
        },
      },
      "&:active": {
        backgroundColor: theme.palette.primary.lighter,
      },

      // The square, deliberately not the root — see the entry comment.
      [`& .${CHECKBOX_BOX_CLASS}`]: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // The hairline sits inside the declared size, so a 24px square renders at 24, not 26.
        boxSizing: "border-box",
        borderRadius: theme.shape.small,
        // Longhands: jsdom's CSSOM does not expand a `border` shorthand.
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: theme.palette.border.secondary,
        backgroundColor: theme.palette.background.paper,
      },
      // On the mark, so the root's `color` never bleeds into it through `currentColor`.
      [`& .${CHECKBOX_GLYPH_CLASS}`]: {
        color: theme.palette.primary.contrastText,
      },
      // Two keys, not one grouped selector: a selector *list* makes `specificity()` in
      // `__tests__/css-cascade.ts` count six classes instead of three.
      "&.Mui-checked": {
        [`& .${CHECKBOX_BOX_CLASS}`]: {
          backgroundColor: theme.palette.primary.main,
          // Not `borderWidth: 0` — the 1px keeps the padding box identical across states.
          borderColor: "transparent",
        },
      },
      "&.MuiCheckbox-indeterminate": {
        [`& .${CHECKBOX_BOX_CLASS}`]: {
          backgroundColor: theme.palette.primary.main,
          borderColor: "transparent",
        },
      },
      // After the selected blocks on purpose — see the entry comment.
      "&.Mui-disabled": {
        [`& .${CHECKBOX_BOX_CLASS}`]: {
          backgroundColor: theme.palette.grey[100],
          borderColor: theme.palette.border.secondary,
        },
        [`& .${CHECKBOX_GLYPH_CLASS}`]: {
          color: theme.palette.text.disabled,
        },
      },
      "&.Mui-focusVisible": {
        backgroundColor: theme.palette.primary.lighter,
        // Not in Figma, whose focused cell is the halo alone — about 1.07:1 against white, and identical
        // to hover. On the **box**, not the root, which would trace the halo's circle instead of the
        // square the user is looking at.
        [`& .${CHECKBOX_BOX_CLASS}`]: {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      },
    }),
  },
};
